import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Panel } from "@/components/Panel";
import { Plus, ExternalLink, Copy, Trash2, Loader2, Sparkles, X, ArrowLeft, BarChart3, Eye, MousePointerClick } from "lucide-react";
import { toast } from "sonner";

const fmtDate = (iso) => iso ? new Date(iso).toLocaleString() : "—";

function ABMForm({ initial, walkthroughs, onSaved, onCancel }) {
  const isEdit = !!initial;
  const [form, setForm] = useState(() => ({
    slug: initial?.slug || "",
    account_name: initial?.account_name || "",
    account_logo_url: initial?.account_logo_url || "",
    headline: initial?.headline || "",
    subhead: initial?.subhead || "",
    body: initial?.body || "",
    walkthrough_id: initial?.walkthrough_id || "",
    cta_url: initial?.cta_url || "",
    cta_label: initial?.cta_label || "Book a working session",
    roi_enabled: initial?.roi_enabled ?? false,
    // Leave labels/defaults blank on new pages so backend stamps them in the
    // operator's workspace currency (so a Nigerian owner gets "(₦M)" / 750,000
    // default, not "($M)" / 500). On edits we preserve what was saved.
    roi_revenue_label: initial?.roi_revenue_label || "",
    roi_revenue_default: initial?.roi_revenue_default ?? null,
    roi_revenue_min: initial?.roi_revenue_min ?? null,
    roi_revenue_max: initial?.roi_revenue_max ?? null,
    roi_leak_label: initial?.roi_leak_label || "",
    roi_leak_default: initial?.roi_leak_default ?? 4,
    roi_leak_min: initial?.roi_leak_min ?? 1,
    roi_leak_max: initial?.roi_leak_max ?? 10,
    roi_recovery_pct: initial?.roi_recovery_pct ?? 60,
    roi_output_label: initial?.roi_output_label || "",
  }));
  const [saving, setSaving] = useState(false);
  const [drafting, setDrafting] = useState(false);
  const [error, setError] = useState("");

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const draft = async () => {
    if (!form.account_name) { setError("Add the account name first."); return; }
    setDrafting(true); setError("");
    try {
      const r = await api.post("/abm/draft-copy", {
        account_name: form.account_name,
        walkthrough_id: form.walkthrough_id || null,
        angle: "revenue map",
        tone: "bold",
      });
      setForm((f) => ({ ...f, headline: r.data.headline, subhead: r.data.subhead, body: r.data.body }));
      toast.success("DARA drafted the page copy.");
    } catch (e) {
      setError(e?.response?.data?.detail || "DARA couldn't draft right now.");
    } finally {
      setDrafting(false);
    }
  };

  const save = async (e) => {
    e.preventDefault();
    setSaving(true); setError("");
    try {
      const payload = { ...form, walkthrough_id: form.walkthrough_id || null, account_logo_url: form.account_logo_url || null };
      if (isEdit) {
        const { slug, ...rest } = payload;
        await api.patch(`/abm/${initial.slug}`, rest);
      } else {
        await api.post("/abm", payload);
      }
      toast.success(isEdit ? "Page updated." : "ABM page created.");
      onSaved?.();
    } catch (err) {
      setError(err?.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-3" data-testid="abm-form">
      {!isEdit && (
        <div>
          <label className="font-mono text-[9px] uppercase tracking-widest text-zinc-500">SLUG (becomes /abm/{form.slug || "your-slug"})</label>
          <input
            value={form.slug}
            onChange={(e) => set("slug", e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, ""))}
            placeholder="acme-q3-pilot"
            className="w-full bg-black border border-zinc-800 px-3 py-2 font-mono text-[12px] text-white"
            data-testid="abm-form-slug"
            required
          />
        </div>
      )}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <div>
          <label className="font-mono text-[9px] uppercase tracking-widest text-zinc-500">ACCOUNT NAME</label>
          <input value={form.account_name} onChange={(e) => set("account_name", e.target.value)} placeholder="Acme Global" className="w-full bg-black border border-zinc-800 px-3 py-2 font-mono text-[12px]" data-testid="abm-form-account" required />
        </div>
        <div>
          <label className="font-mono text-[9px] uppercase tracking-widest text-zinc-500">LOGO URL (optional)</label>
          <input value={form.account_logo_url} onChange={(e) => set("account_logo_url", e.target.value)} placeholder="https://logo.clearbit.com/acme.com" className="w-full bg-black border border-zinc-800 px-3 py-2 font-mono text-[12px]" data-testid="abm-form-logo" />
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <div>
          <label className="font-mono text-[9px] uppercase tracking-widest text-zinc-500">WALKTHROUGH (optional)</label>
          <select value={form.walkthrough_id} onChange={(e) => set("walkthrough_id", e.target.value)} className="w-full bg-black border border-zinc-800 px-3 py-2 font-mono text-[12px] text-white" data-testid="abm-form-walkthrough">
            <option value="">— None —</option>
            {walkthroughs.map((w) => (
              <option key={w.id} value={w.id}>{w.title} · {Math.round(w.duration_s)}s</option>
            ))}
          </select>
        </div>
        <div className="flex items-end">
          <button type="button" onClick={draft} disabled={drafting || !form.account_name} className="border border-[#FFB000]/40 text-[#FFB000] px-4 py-2 font-mono text-[11px] uppercase tracking-widest hover:bg-[#FFB000]/10 disabled:opacity-50 flex items-center gap-2 w-full justify-center" data-testid="abm-form-claude-draft">
            {drafting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Sparkles className="w-3.5 h-3.5" />}
            {drafting ? "DRAFTING…" : "DRAFT WITH DARA"}
          </button>
        </div>
      </div>

      <div>
        <label className="font-mono text-[9px] uppercase tracking-widest text-zinc-500">HEADLINE</label>
        <input value={form.headline} onChange={(e) => set("headline", e.target.value)} placeholder="DARA × Acme — your Q3 revenue map" className="w-full bg-black border border-zinc-800 px-3 py-2 text-base text-white" data-testid="abm-form-headline" required />
      </div>
      <div>
        <label className="font-mono text-[9px] uppercase tracking-widest text-zinc-500">SUBHEAD</label>
        <input value={form.subhead} onChange={(e) => set("subhead", e.target.value)} placeholder="One-sentence pitch on why DARA is relevant for THIS account." className="w-full bg-black border border-zinc-800 px-3 py-2 text-sm text-white" data-testid="abm-form-subhead" />
      </div>
      <div>
        <label className="font-mono text-[9px] uppercase tracking-widest text-zinc-500">BODY (2 short paragraphs)</label>
        <textarea value={form.body} onChange={(e) => set("body", e.target.value)} rows={9} className="w-full bg-black border border-zinc-800 px-3 py-2 text-sm text-zinc-100 leading-relaxed" data-testid="abm-form-body" required />
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <div>
          <label className="font-mono text-[9px] uppercase tracking-widest text-zinc-500">CTA URL</label>
          <input value={form.cta_url} onChange={(e) => set("cta_url", e.target.value)} placeholder="https://calendly.com/dara/acme" className="w-full bg-black border border-zinc-800 px-3 py-2 font-mono text-[12px]" data-testid="abm-form-cta-url" required />
        </div>
        <div>
          <label className="font-mono text-[9px] uppercase tracking-widest text-zinc-500">CTA LABEL</label>
          <input value={form.cta_label} onChange={(e) => set("cta_label", e.target.value)} className="w-full bg-black border border-zinc-800 px-3 py-2 font-mono text-[12px]" data-testid="abm-form-cta-label" required />
        </div>
      </div>

      {/* ROI Calculator config */}
      <div className="border border-zinc-900 p-3 bg-black/30">
        <label className="flex items-center gap-2 cursor-pointer">
          <input type="checkbox" checked={form.roi_enabled} onChange={(e) => set("roi_enabled", e.target.checked)} className="accent-[#FFB000] w-4 h-4" data-testid="abm-form-roi-toggle" />
          <span className="font-mono text-[11px] uppercase tracking-widest text-[#FFB000]">// EMBED ROI CALCULATOR</span>
          <span className="font-mono text-[10px] text-zinc-500">— prospect plugs in their numbers; engagement tracked per session</span>
        </label>
        {form.roi_enabled && (
          <div className="mt-3 grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <label className="font-mono text-[9px] uppercase tracking-widest text-zinc-500">REVENUE LABEL</label>
              <input value={form.roi_revenue_label} onChange={(e) => set("roi_revenue_label", e.target.value)} className="w-full bg-black border border-zinc-800 px-3 py-2 font-mono text-[12px]" data-testid="abm-form-roi-revenue-label" />
            </div>
            <div>
              <label className="font-mono text-[9px] uppercase tracking-widest text-zinc-500">REVENUE DEFAULT (local M) / MIN / MAX</label>
              <div className="flex gap-1">
                <input type="number" value={form.roi_revenue_default ?? ""} onChange={(e) => set("roi_revenue_default", e.target.value === "" ? null : Number(e.target.value))} placeholder="auto" className="w-1/3 bg-black border border-zinc-800 px-2 py-2 font-mono text-[11px]" data-testid="abm-form-roi-rev-default" />
                <input type="number" value={form.roi_revenue_min ?? ""} onChange={(e) => set("roi_revenue_min", e.target.value === "" ? null : Number(e.target.value))} placeholder="auto" className="w-1/3 bg-black border border-zinc-800 px-2 py-2 font-mono text-[11px]" data-testid="abm-form-roi-rev-min" />
                <input type="number" value={form.roi_revenue_max ?? ""} onChange={(e) => set("roi_revenue_max", e.target.value === "" ? null : Number(e.target.value))} placeholder="auto" className="w-1/3 bg-black border border-zinc-800 px-2 py-2 font-mono text-[11px]" data-testid="abm-form-roi-rev-max" />
              </div>
              <div className="font-mono text-[9px] text-zinc-600 mt-1">Leave blank — DARA picks sensible values for your workspace currency.</div>
            </div>
            <div>
              <label className="font-mono text-[9px] uppercase tracking-widest text-zinc-500">LEAK LABEL</label>
              <input value={form.roi_leak_label} onChange={(e) => set("roi_leak_label", e.target.value)} className="w-full bg-black border border-zinc-800 px-3 py-2 font-mono text-[12px]" data-testid="abm-form-roi-leak-label" />
            </div>
            <div>
              <label className="font-mono text-[9px] uppercase tracking-widest text-zinc-500">LEAK DEFAULT (%) / MIN / MAX</label>
              <div className="flex gap-1">
                <input type="number" step="0.1" value={form.roi_leak_default} onChange={(e) => set("roi_leak_default", Number(e.target.value))} className="w-1/3 bg-black border border-zinc-800 px-2 py-2 font-mono text-[11px]" />
                <input type="number" step="0.1" value={form.roi_leak_min} onChange={(e) => set("roi_leak_min", Number(e.target.value))} className="w-1/3 bg-black border border-zinc-800 px-2 py-2 font-mono text-[11px]" />
                <input type="number" step="0.1" value={form.roi_leak_max} onChange={(e) => set("roi_leak_max", Number(e.target.value))} className="w-1/3 bg-black border border-zinc-800 px-2 py-2 font-mono text-[11px]" />
              </div>
            </div>
            <div>
              <label className="font-mono text-[9px] uppercase tracking-widest text-zinc-500">DARA RECOVERY OF LEAK (%)</label>
              <input type="number" step="1" value={form.roi_recovery_pct} onChange={(e) => set("roi_recovery_pct", Number(e.target.value))} className="w-full bg-black border border-zinc-800 px-3 py-2 font-mono text-[12px]" data-testid="abm-form-roi-recovery-pct" />
            </div>
            <div>
              <label className="font-mono text-[9px] uppercase tracking-widest text-zinc-500">OUTPUT LABEL</label>
              <input value={form.roi_output_label} onChange={(e) => set("roi_output_label", e.target.value)} className="w-full bg-black border border-zinc-800 px-3 py-2 font-mono text-[12px]" />
            </div>
          </div>
        )}
      </div>
      {error && <div className="text-[#FF3B30] font-mono text-[11px]">{error}</div>}
      <div className="flex justify-end gap-2 pt-2">
        <button type="button" onClick={onCancel} className="border border-zinc-800 px-4 py-2 font-mono text-[11px] uppercase tracking-widest hover:bg-zinc-900" data-testid="abm-form-cancel">CANCEL</button>
        <button type="button" onClick={save} disabled={saving} className="bg-[#FFB000] text-black font-bold font-mono text-[11px] uppercase tracking-widest px-5 py-2 hover:bg-[#E59E00] disabled:opacity-50 flex items-center gap-2" data-testid="abm-form-save">
          {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : null}
          {isEdit ? "UPDATE PAGE" : "CREATE PAGE"}
        </button>
      </div>
    </div>
  );
}

function ABMAnalyticsView({ slug, onBack }) {
  const [data, setData] = useState(null);
  useEffect(() => { api.get(`/abm/${slug}/analytics`).then((r) => setData(r.data)).catch(() => setData({ error: "Couldn't load" })); }, [slug]);
  if (!data) return <div className="text-zinc-500 font-mono text-[11px] flex items-center gap-2"><Loader2 className="w-3.5 h-3.5 animate-spin text-[#FFB000]" /> Loading…</div>;
  if (data.error) return <div className="text-[#FF3B30] font-mono text-[11px]">{data.error}</div>;
  const { totals = {}, regions = [], sessions = [], roi = null, currency_symbol = "$" } = data;
  const sym = currency_symbol || "$";
  const fmtMoney = (n) => {
    if (!n) return `${sym}0`;
    if (n >= 1e9) return `${sym}${(n / 1e9).toFixed(1)}B`;
    if (n >= 1e6) return `${sym}${(n / 1e6).toFixed(1)}M`;
    if (n >= 1e3) return `${sym}${(n / 1e3).toFixed(0)}K`;
    return `${sym}${Math.round(n).toLocaleString()}`;
  };
  return (
    <div className="space-y-4" data-testid="abm-analytics-view">
      <div className="flex items-center gap-3">
        <button onClick={onBack} className="border border-zinc-800 px-3 py-1.5 font-mono text-[10px] uppercase tracking-widest hover:bg-zinc-900 flex items-center gap-1.5" data-testid="abm-analytics-back"><ArrowLeft className="w-3 h-3" /> BACK</button>
        <h2 className="font-heading font-bold text-xl">Analytics · {data.account_name} <span className="text-zinc-500">· /{slug}</span></h2>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
        {[
          ["SESSIONS", totals.sessions, "text-[#FFB000]"],
          ["UNIQUE IPS", totals.unique_ips, "text-white"],
          ["CTA CLICKS", totals.cta_clicks, "text-[#00E676]"],
          ["100% SCROLL", totals.completed_scroll, "text-[#FFB000]"],
          ["AVG SCROLL %", `${totals.avg_scroll_max}%`, "text-white"],
        ].map(([l, v, c]) => (
          <div key={l} className="border border-zinc-900 p-3 bg-black/40">
            <div className="font-mono text-[9px] uppercase tracking-widest text-zinc-500">{l}</div>
            <div className={`font-heading font-black text-2xl mt-1 ${c}`}>{v}</div>
          </div>
        ))}
      </div>
      {regions.length > 0 && (
        <div>
          <div className="font-mono text-[9px] uppercase tracking-widest text-zinc-500 mb-2">REGIONS</div>
          <div className="flex flex-wrap gap-1.5">
            {regions.map((r) => <span key={r.region} className="border border-zinc-800 px-2 py-1 font-mono text-[10px]">{r.region} <span className="text-[#FFB000] ml-1">×{r.count}</span></span>)}
          </div>
        </div>
      )}
      <div className="border border-zinc-900 max-h-[400px] overflow-y-auto">
        <table className="w-full text-sm">
          <thead className="bg-black/60 sticky top-0">
            <tr>{["Region", "Scroll Max", "CTA", "Re-visits", "First seen", "Last seen"].map((h) => <th key={h} className="text-left font-mono text-[9px] uppercase tracking-widest text-zinc-500 px-3 py-2">{h}</th>)}</tr>
          </thead>
          <tbody>
            {sessions.length === 0 && <tr><td colSpan={6} className="px-3 py-6 text-center text-zinc-600 font-mono text-[11px]">No sessions yet — share the URL.</td></tr>}
            {sessions.map((s) => (
              <tr key={s.id} className="border-t border-zinc-900 hover:bg-zinc-900/30">
                <td className="px-3 py-2 font-mono text-[11px] text-zinc-300">{s.region}</td>
                <td className="px-3 py-2 font-mono text-[11px]"><span className={s.scroll_max >= 100 ? "text-[#00E676]" : s.scroll_max >= 50 ? "text-[#FFB000]" : "text-zinc-500"}>{s.scroll_max}%</span></td>
                <td className="px-3 py-2 font-mono text-[11px]">{s.cta_clicks > 0 ? <span className="text-[#00E676]">×{s.cta_clicks}</span> : <span className="text-zinc-600">—</span>}</td>
                <td className="px-3 py-2 font-mono text-[11px] text-zinc-300">×{s.rewatch_count_for_ip}</td>
                <td className="px-3 py-2 font-mono text-[10px] text-zinc-500">{fmtDate(s.first_seen_at)}</td>
                <td className="px-3 py-2 font-mono text-[10px] text-zinc-500">{fmtDate(s.last_seen_at)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {roi && (
        <div className="border border-[#FFB000]/30 bg-[#FFB000]/5 p-4" data-testid="abm-analytics-roi">
          <div className="font-mono text-[9px] uppercase tracking-widest text-[#FFB000] mb-3">// ROI CALCULATOR · ENGAGEMENT</div>
          <div className="grid grid-cols-3 gap-2 mb-4">
            {[
              ["ENGAGED SESSIONS", roi.engaged_sessions, "text-white"],
              ["ENGAGEMENT RATE", `${roi.engagement_rate_pct}%`, "text-[#FFB000]"],
              ["MAX COMPUTED LIFT", fmtMoney(roi.max_computed), "text-[#00E676]"],
            ].map(([l, v, c]) => (
              <div key={l} className="border border-zinc-900 p-3 bg-black/40">
                <div className="font-mono text-[9px] uppercase tracking-widest text-zinc-500">{l}</div>
                <div className={`font-heading font-black text-2xl mt-1 ${c}`}>{v}</div>
              </div>
            ))}
          </div>
          {roi.samples.length === 0 ? (
            <div className="font-mono text-[11px] text-zinc-500">No prospects have plugged in their numbers yet.</div>
          ) : (
            <div className="border border-zinc-900 overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-black/60">
                  <tr>{["Region", "Their revenue", "Their leak %", "Computed lift", "Edits", "When"].map((h) => <th key={h} className="text-left font-mono text-[9px] uppercase tracking-widest text-zinc-500 px-3 py-2">{h}</th>)}</tr>
                </thead>
                <tbody>
                  {roi.samples.map((s) => (
                    <tr key={s.session_id} className="border-t border-zinc-900 hover:bg-zinc-900/30">
                      <td className="px-3 py-2 font-mono text-[11px] text-zinc-300">{s.region}</td>
                      <td className="px-3 py-2 font-mono text-[11px] text-white">{sym}{s.roi_revenue?.toLocaleString()}M</td>
                      <td className="px-3 py-2 font-mono text-[11px] text-[#FFB000]">{s.roi_leak_pct?.toFixed(1)}%</td>
                      <td className="px-3 py-2 font-mono text-[11px] text-[#00E676]">{fmtMoney(s.roi_computed)}</td>
                      <td className="px-3 py-2 font-mono text-[11px] text-zinc-400">×{s.roi_changes}</td>
                      <td className="px-3 py-2 font-mono text-[10px] text-zinc-500">{fmtDate(s.roi_engaged_at)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          <div className="font-mono text-[10px] text-zinc-500 mt-3">
            👉 The highest-quality intent signal: prospects who plugged their actual numbers into the calculator are
            already projecting DARA&apos;s value internally. Reach out today.
          </div>
        </div>
      )}
    </div>
  );
}

export default function ABMAdmin() {
  const [items, setItems] = useState(null);
  const [walkthroughs, setWalkthroughs] = useState([]);
  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState(null);
  const [analyticsFor, setAnalyticsFor] = useState(null);

  const load = async () => {
    try { const r = await api.get("/abm"); setItems(r.data.items); } catch { setItems([]); }
    try { const w = await api.get("/walkthroughs"); setWalkthroughs(w.data.items || []); } catch { setWalkthroughs([]); }
  };
  useEffect(() => { load(); }, []);

  const copyUrl = async (slug) => {
    const url = `${window.location.origin}/abm/${slug}`;
    try { await navigator.clipboard.writeText(url); toast.success("URL copied."); } catch { toast.error("Couldn't copy."); }
  };
  const remove = async (slug) => {
    if (!window.confirm(`Delete /abm/${slug}? Anyone with the URL will get a 404.`)) return;
    try { await api.delete(`/abm/${slug}`); toast.success("Deleted."); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Delete failed"); }
  };

  return (
    <div className="p-6 space-y-6" data-testid="abm-admin-page">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">MODULE · ABM</div>
          <h1 className="font-heading font-black text-4xl tracking-tighter uppercase mt-1">// ABM PAGES</h1>
          <div className="font-mono text-[11px] text-zinc-500 mt-1">
            One-click account-based marketing pages. Drop a slug, paste in your prospects, get full attribution.
          </div>
        </div>
        {!creating && !editing && !analyticsFor && (
          <button onClick={() => setCreating(true)} data-testid="abm-create-btn" className="bg-[#FFB000] text-black font-bold font-mono text-[11px] uppercase tracking-widest px-5 py-2.5 hover:bg-[#E59E00] flex items-center gap-2">
            <Plus className="w-3.5 h-3.5" /> NEW ABM PAGE
          </button>
        )}
      </div>

      {creating && (
        <Panel title="// NEW ABM PAGE">
          <div className="p-4">
            <ABMForm walkthroughs={walkthroughs} onSaved={() => { setCreating(false); load(); }} onCancel={() => setCreating(false)} />
          </div>
        </Panel>
      )}

      {editing && (
        <Panel title={`// EDIT /abm/${editing.slug}`}>
          <div className="p-4">
            <ABMForm initial={editing} walkthroughs={walkthroughs} onSaved={() => { setEditing(null); load(); }} onCancel={() => setEditing(null)} />
          </div>
        </Panel>
      )}

      {analyticsFor && (
        <Panel title="// ANALYTICS">
          <div className="p-4">
            <ABMAnalyticsView slug={analyticsFor} onBack={() => setAnalyticsFor(null)} />
          </div>
        </Panel>
      )}

      {!creating && !editing && !analyticsFor && (
        <Panel title="// LIBRARY" testId="abm-library-panel">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-zinc-900">
                  {["Slug", "Account", "Views", "CTA Clicks", "Last viewed", "Actions"].map((h) => (
                    <th key={h} className="text-left font-mono text-[10px] uppercase tracking-widest text-zinc-500 px-4 py-3">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {items === null && <tr><td colSpan={6} className="px-4 py-10 text-center text-zinc-500"><Loader2 className="w-4 h-4 animate-spin inline mr-2" />Loading…</td></tr>}
                {items && items.length === 0 && <tr><td colSpan={6} className="px-4 py-12 text-center text-zinc-500" data-testid="abm-empty"><div className="font-mono text-[11px] uppercase tracking-widest mb-2">No ABM pages yet.</div><div className="text-xs text-zinc-600">Click NEW ABM PAGE to create your first prospect-targeted page.</div></td></tr>}
                {items && items.map((p) => (
                  <tr key={p.id} className="border-b border-zinc-900 hover:bg-zinc-900/30" data-testid={`abm-row-${p.slug}`}>
                    <td className="px-4 py-3"><div className="font-mono text-[11px] text-[#FFB000]">/abm/{p.slug}</div></td>
                    <td className="px-4 py-3"><div className="font-heading font-bold">{p.account_name}</div><div className="font-mono text-[10px] text-zinc-600 truncate max-w-[280px]">{p.headline}</div></td>
                    <td className="px-4 py-3 font-mono text-[11px]"><Eye className="w-3 h-3 inline mr-1 text-zinc-500" />{p.view_count}</td>
                    <td className="px-4 py-3 font-mono text-[11px]"><MousePointerClick className="w-3 h-3 inline mr-1 text-[#00E676]" /><span className={p.cta_click_count > 0 ? "text-[#00E676]" : "text-zinc-500"}>{p.cta_click_count}</span></td>
                    <td className="px-4 py-3 font-mono text-[10px] text-zinc-500">{p.last_viewed_at ? fmtDate(p.last_viewed_at) : "—"}</td>
                    <td className="px-4 py-3"><div className="flex gap-1">
                      <a href={`/abm/${p.slug}`} target="_blank" rel="noopener noreferrer" className="p-1.5 text-zinc-500 hover:text-[#00E676]" title="Open public page"><ExternalLink className="w-3.5 h-3.5" /></a>
                      <button onClick={() => copyUrl(p.slug)} className="p-1.5 text-zinc-500 hover:text-[#FFB000]" data-testid={`abm-copy-${p.slug}`} title="Copy URL"><Copy className="w-3.5 h-3.5" /></button>
                      <button onClick={() => setAnalyticsFor(p.slug)} className="p-1.5 text-zinc-500 hover:text-[#FFB000]" data-testid={`abm-analytics-${p.slug}`} title="Analytics"><BarChart3 className="w-3.5 h-3.5" /></button>
                      <button onClick={() => setEditing(p)} className="p-1.5 text-zinc-500 hover:text-white" data-testid={`abm-edit-${p.slug}`} title="Edit">✎</button>
                      <button onClick={() => remove(p.slug)} className="p-1.5 text-zinc-500 hover:text-[#FF3B30]" data-testid={`abm-delete-${p.slug}`} title="Delete"><Trash2 className="w-3.5 h-3.5" /></button>
                    </div></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Panel>
      )}
    </div>
  );
}
